/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.org/docs/browser-apis/
 */

import React, { useEffect } from 'react';
import { CurrencyProvider } from 'src/contexts/CurrencyContext';
import { captureReferral, decorateReferralLink } from 'src/helpers/referral';

/**
 * Marks `window.__HYDRATED__` once the whole page tree has committed and
 * every component's event handlers are wired up (this component is the
 * outermost element, so — per React's bottom-up effect order — its own
 * effect always fires last, after every nested component's). e2e tests
 * (see cypress/support/e2e.js) wait on this instead of racing hydration on
 * interactive elements that have no native fallback (rc-slider, react-select,
 * role="button" cards navigating via onClick).
 */
const HydrationMarker = ({ children }) => {
  useEffect(() => {
    // eslint-disable-next-line no-underscore-dangle
    window.__HYDRATED__ = true;
  }, []);
  return children;
};

export const wrapRootElement = ({ element }) => (
  <CurrencyProvider>
    <HydrationMarker>{element}</HydrationMarker>
  </CurrencyProvider>
);

// Scroll to top on route changes, but preserve hash anchor behavior
export const shouldUpdateScroll = ({ routerProps }) => {
  if (routerProps?.location?.hash) {
    return true;
  }

  return [0, 0];
};

// Referral capture runs once after the first render, when the browser is idle,
// so it never slows the page
export const onInitialClientRender = () => {
  const whenIdle = window.requestIdleCallback || ((callback) => setTimeout(callback, 1));
  whenIdle(() => captureReferral());

  // panel links (Cockpit content) carry the stored ref to the other domain
  document.addEventListener('click', (event) => {
    const anchor = event.target.closest && event.target.closest('a[href]');
    if (anchor) decorateReferralLink(anchor);
  }, true);
};
